import React, { useState } from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Trans } from 'react-i18next';

import OrderCommentInput from '../common/_inputs/OrderComment';
import InputFile from '../common/_inputs/InputFile';
import OrderModalWrapper from './OrderModalWrapper';
import {
  CustomDeliveryMessage,
  LocationAlertTutorialLink,
  LocationButton,
  LocationInput,
} from './components';
import PickupOrderInputFileLabel from './components/PickupOrderInputFileLabel';
import DynamicRecipientFields from './components/DynamicRecipientFields';
import SignatureModal from '../common/components/SignatureModal';
import { MAX_ATTEMPTS_LOCATION } from '../../config/consts/commons';
import { Box, Grid2 as Grid, TextField } from '@mui/material';
import { useOrdersToDeliverModal } from './hooks';
import {
  RECIPIENT_NAME_MAX_LENGTH,
  VALID_RECIPIENT_NAME_REGEX_PATTERN,
} from '../../config/consts/orders';
import { normalizeName } from '../../utils';
import env from '../../config/env';

const OrdersToDeliverModal = () => {
  const {
    t,
    order,
    config,
    computeRules,
    setLocation,
    customLocation,
    setCustomLocation,
    locationStatus,
    setLocationStatus,
    attempts,
    setAttempts,
    errorMessage,
    setErrorMessage,
    buttonLabel,
    setComment,
    setCommentPickedUpOrder,
    recipient,
    setRecipient,
    recipientDocument,
    setRecipientDocument,
    recipientRelationship,
    setRecipientRelationship,
    signatureModalOpen,
    setSignatureModalOpen,
    signatureData,
    isSubmitting,
    showSubmitError,
    wasPickedUp,
    handleSubmit,
    handleSignatureConfirm,
    resetModal,
  } = useOrdersToDeliverModal();

  const [file, setFile] = useState(null);
  const [filePickedUpOrder, setFilePickedUpOrder] = useState(null);
  const [isFileCompressing, setIsFileCompressing] = useState(false);

  const submitDisabled = computeRules({
    file,
    filePickedUpOrder,
    isFileCompressing,
  });

  const imageCopywrite = (
    <Box
      component="a"
      href={env.DELIVERED_PICTURE_SAMPLE_URL}
      color="inherit"
      target="_blank"
      rel="noreferrer"
    >
      <Trans
        t={t}
        ns="description"
        i18nKey="orders.deliver.modal.image_copyright"
      >
        Attach the photo of the{' '}
        <span className="text-primary fw-bold">customer</span> next to the order{' '}
        as shown in this example
        <OpenInNewIcon className="fa-sm" />.
      </Trans>
    </Box>
  );

  return (
    <OrderModalWrapper
      handleClose={resetModal}
      handleSubmit={() => handleSubmit({ file, filePickedUpOrder })}
      buttonLabel={buttonLabel}
      isSubmitting={isSubmitting}
      showSubmitError={showSubmitError}
      submitButtonDisabled={submitDisabled}
      orderCode={
        order.related_order && !wasPickedUp
          ? order.related_order.order_code
          : order.order_code
      }
    >
      {order.related_order && !wasPickedUp && (
        <Box
          sx={{
            pointerEvents: isSubmitting ? 'none' : 'auto',
            userSelect: isSubmitting ? 'none' : 'auto',
            opacity: isSubmitting ? 0.6 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              width: '100%',
            }}
          >
            <InputFile
              label={<PickupOrderInputFileLabel />}
              onFileReady={setFilePickedUpOrder}
              onProcessingChange={setIsFileCompressing}
              inputId="file-pickup-order"
            />

            <LocationButton
              setLocation={setLocation}
              locationStatus={locationStatus}
              setLocationStatus={setLocationStatus}
              setErrorMessage={setErrorMessage}
              attempts={attempts}
              setAttempts={setAttempts}
            />

            {errorMessage && locationStatus === 'error' && (
              <LocationAlertTutorialLink />
            )}
            {locationStatus === 'error' &&
              attempts >= MAX_ATTEMPTS_LOCATION && (
                <LocationInput
                  customLocation={customLocation}
                  setCustomLocation={setCustomLocation}
                />
              )}

            <OrderCommentInput
              setComment={(payload) => setCommentPickedUpOrder(payload)}
            />
          </Box>
        </Box>
      )}

      {(!order.related_order || wasPickedUp) && (
        <Grid
          container
          spacing={3}
          sx={{
            pointerEvents: isSubmitting ? 'none' : 'auto',
            userSelect: isSubmitting ? 'none' : 'auto',
            opacity: isSubmitting ? 0.6 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          <Grid size={12}>
            <CustomDeliveryMessage idEcommerce={order.ecommerce_name} />
          </Grid>

          <Grid size={12}>
            <InputFile
              label={imageCopywrite}
              onFileReady={setFile}
              onProcessingChange={setIsFileCompressing}
              inputId="file-deliver-order"
            />
          </Grid>

          <Grid size={12}>
            <TextField
              label={t('orders.deliver.addressee_name', { ns: 'description' })}
              placeholder={t('orders.deliver.addressee_name_placeholder', {
                ns: 'description',
                defaultValue: 'Nome completo',
              })}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              fullWidth
              variant="outlined"
              inputProps={{ maxLength: RECIPIENT_NAME_MAX_LENGTH }}
              error={
                recipient.length >= RECIPIENT_NAME_MAX_LENGTH ||
                (recipient.length > 0 &&
                  !VALID_RECIPIENT_NAME_REGEX_PATTERN.test(
                    normalizeName(recipient)
                  ))
              }
              helperText={
                recipient.length > 0 &&
                !VALID_RECIPIENT_NAME_REGEX_PATTERN.test(
                  normalizeName(recipient)
                )
                  ? t('validation.invalid_name', {
                      ns: 'validation',
                      defaultValue: 'Formato de nombre inválido',
                    })
                  : ''
              }
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.6)' },
              }}
            />
          </Grid>

          <DynamicRecipientFields
            config={config}
            values={{ recipientDocument, recipientRelationship, signatureData }}
            handlers={{
              onRecipientDocumentChange: (e) =>
                setRecipientDocument(e.target.value),
              onRecipientRelationshipChange: (e) =>
                setRecipientRelationship(e.target.value),
              onOpenSignatureModal: () => setSignatureModalOpen(true),
            }}
          />

          <Grid size={12}>
            <LocationButton
              setLocation={setLocation}
              locationStatus={locationStatus}
              setLocationStatus={setLocationStatus}
              setErrorMessage={setErrorMessage}
              attempts={attempts}
              setAttempts={setAttempts}
            />

            {errorMessage && locationStatus === 'error' && (
              <LocationAlertTutorialLink />
            )}
            {locationStatus === 'error' &&
              attempts >= MAX_ATTEMPTS_LOCATION && (
                <LocationInput
                  customLocation={customLocation}
                  setCustomLocation={setCustomLocation}
                />
              )}
          </Grid>

          <Grid size={12}>
            <OrderCommentInput setComment={(payload) => setComment(payload)} />
          </Grid>
        </Grid>
      )}

      <SignatureModal
        open={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onConfirm={handleSignatureConfirm}
        initialSignature={signatureData}
      />
    </OrderModalWrapper>
  );
};

export default OrdersToDeliverModal;
